'use client'

import { Button } from '@/components/ui/button'
import { useCartQuery } from '@/hooks/react-query/cart/use-cart'
import { useExchangeGift } from '@/hooks/react-query/cart/use-exchange-gift'
import { useIsBuy1Get1InGiftShopCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { CheckCircle2, Gift } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useMemo, useState } from 'react'
import { useProductCardContext } from './context/product-card-context'
import { GiftSelectionModal } from './gift-selection-modal'

export const GiftExchangeButton = () => {
  const { product } = useProductCardContext()
  const searchParams = useSearchParams()
  const mainProductIndexId = searchParams.get('main_product_id')
  const { mutateAsync: exchangeGift } = useExchangeGift()
  const { data: cartData } = useCartQuery()
  const t = useTranslations()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isBuy1Get1InGiftShopActive = useIsBuy1Get1InGiftShopCampaignActive()

  // Tìm main product và check đã chọn quà chưa
  // Bắt buộc phải có mainProductIndexId từ query params
  const { mainProduct, isSelecting, isGiftSelected } = useMemo(() => {
    // Nếu không có mainProductIndexId, không tìm main product
    if (!mainProductIndexId) {
      return {
        mainProduct: null,
        isSelecting: false,
        isGiftSelected: false,
      }
    }

    const main = cartData?.items?.find(
      (item) => item.index_id === mainProductIndexId
    )
    const hasExistingGift = (main?.nestedGift?.length ?? 0) > 0

    // Check xem quà hiện tại (product) đã được chọn chưa
    // Chỉ check product_id vì variant có thể khác
    const isSelected =
      main?.nestedGift?.some((gift) => {
        const isSameProduct = gift.id === product.id
        return isSameProduct
      }) === true

    return {
      mainProduct: main || null,
      isSelecting: !hasExistingGift, // Chọn quà nếu chưa có quà
      isGiftSelected: isSelected || false, // Quà hiện tại đã được chọn
    }
  }, [cartData, mainProductIndexId, product.id])

  // Check if button should be disabled
  // - Disable nếu không có mainProductIndexId hoặc mainProduct
  // - Disable nếu giá quà > giá main product (check với giá min của product)
  const { isDisabled } = useMemo(() => {
    // Disable nếu không có mainProductIndexId hoặc mainProduct
    if (!mainProductIndexId || !mainProduct) {
      return { isDisabled: true }
    }

    // Check giá quà > giá main product
    // Lấy giá min của product (regular_price từ product card)
    const giftMinPrice = product.regular_price || 0
    const mainProductPrice = mainProduct.regular_price || 0

    if (giftMinPrice > mainProductPrice) {
      return {
        isDisabled: true,
      }
    }

    return { isDisabled: false }
  }, [product, mainProductIndexId, mainProduct])

  // Only show button when buy-1-get-1-in-gift-shop campaign is active
  if (!isBuy1Get1InGiftShopActive) {
    return null
  }

  const handleOpenModal = () => {
    if (isDisabled) return
    setIsModalOpen(true)
  }

  const handleConfirm = async (variantId: string) => {
    if (!mainProductIndexId) {
      throw new Error('Main product index ID is required')
    }

    // Tìm variant từ product để lấy thông tin
    const variant = Object.values(product.mapped_variants || {})
      .flat()
      .find((v) => v.id === variantId)

    if (!variant) {
      throw new Error('Variant not found')
    }

    // Gọi API và throw error nếu có lỗi để modal có thể xử lý
    await exchangeGift({
      giftProductId: product.id,
      giftVariantId: variantId,
      giftProductName: product.title,
      giftImageUrl: variant.thumbnail || product.default_thumbnail || '',
      giftPrice: variant.regular_price || product.regular_price,
      mainProductIndexId: mainProductIndexId,
    })
  }

  // Button text dựa trên trạng thái
  const buttonText = isSelecting
    ? t('gift_shop.select_gift')
    : t('gift_shop.exchange_gift')
  const ariaLabel = isSelecting
    ? t('gift_shop.select_gift')
    : t('gift_shop.exchange_gift')

  // Nếu quà đã được chọn, hiển thị UI "Bạn đã chọn quà này" với icon checked
  if (isGiftSelected) {
    return (
      <div className='!mt-auto'>
        <div className='bg-primary-50 mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-primary-500 px-4 py-2.5'>
          <CheckCircle2 size={16} className='text-primary-500' />
          <span className='font-sans text-sm font-medium text-primary-500'>
            {t('gift_shop.already_selected') || 'Bạn đã chọn quà này'}
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='!mt-auto'>
        <Button
          variant='outline'
          size='default'
          className='mt-3 w-full'
          onClick={handleOpenModal}
          disabled={isDisabled}
          aria-label={ariaLabel}
        >
          <Gift size={16} className='mr-2' />
          {buttonText}
        </Button>
      </div>
      <GiftSelectionModal
        product={product}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onConfirm={handleConfirm}
        isSelecting={isSelecting}
        mainProductPrice={mainProduct?.regular_price}
      />
    </>
  )
}
